import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClient } from "../../services/apiClientService";
import ProductCard from "./ProductCard";
import { getStorefrontPrices, productHasVariantOptions } from "../../utils/productVariants";
import { sortProductsWithFeatured } from "../../utils/featuredProducts";
import { useCart } from "../../context/CartContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import CartMismatchModal from "../common/CartMismatchModal";
import { useVendorStore } from "../../context/VendorStoreContext";
import SelectField from "../common/SelectField";
import Pagination from "../common/Pagination";
import { fetchBannersPublic } from "../../services/cmsClient";
import { useUserAuth } from "../../context/UserAuthContext";

const effectiveSalePrice = (p) => getStorefrontPrices(p).salePrice;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "name", label: "Name A–Z" },
];

const PRODUCTS_PER_PAGE = 12;

/** Identical filter panel to the main shop — keeps UX consistent across the platform. */
function ShopFilterPanel({
  className = "",
  embedded = false,
  filters,
  onFilterChange,
  setFilters,
  allTypes,
  allSaleTypes,
  activeFilterCount,
  onClearFilters,
}) {
  return (
    <div
      className={`rounded-2xl border border-stone-200/90 bg-white/95 shadow-sm backdrop-blur-sm ${className}`}
    >
      {!embedded && (
        <div className="border-b border-stone-100 px-5 py-4 flex items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-bold text-stone-900">Filters</h3>
            <p className="text-xs text-stone-500 mt-0.5">Narrow down what you&apos;re craving</p>
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-xs font-semibold text-rose-600 hover:text-rose-700 whitespace-nowrap"
            >
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      )}

      <div className={`space-y-5 ${embedded ? "p-0" : "p-5"}`}>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
            Search
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              name="search"
              autoComplete="off"
              placeholder="Search products…"
              value={filters.search}
              onChange={onFilterChange}
              className="w-full rounded-xl border border-stone-200 bg-stone-50/80 py-2.5 pl-10 pr-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-rose-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
            Category
          </label>
          <SelectField
            id={`vendor-filter-type-${embedded ? "emb" : "desk"}`}
            name="type"
            value={filters.type}
            onChange={onFilterChange}
            options={[
              { value: "", label: "All categories" },
              ...allTypes.map((t) => ({ value: t, label: t })),
            ]}
          />
          {allTypes.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => setFilters((p) => ({ ...p, type: "" }))}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  !filters.type
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                All
              </button>
              {allTypes.slice(0, 6).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilters((p) => ({ ...p, type: t }))}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition max-w-[140px] truncate ${
                    filters.type === t
                      ? "bg-rose-600 text-white"
                      : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        {allSaleTypes.length > 0 && (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
              Offer type
            </label>
            <SelectField
              id={`vendor-filter-sale-${embedded ? "emb" : "desk"}`}
              name="saleType"
              value={filters.saleType}
              onChange={onFilterChange}
              options={[
                { value: "", label: "All offers" },
                ...allSaleTypes.map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
        )}

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-2">
            Price (₹)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              name="minPrice"
              type="number"
              min="0"
              placeholder="Min"
              value={filters.minPrice}
              onChange={onFilterChange}
              className="w-full rounded-xl border border-stone-200 bg-white py-2.5 px-3 text-sm focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
            <input
              name="maxPrice"
              type="number"
              min="0"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={onFilterChange}
              className="w-full rounded-xl border border-stone-200 bg-white py-2.5 px-3 text-sm focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VendorStorePage() {
  const { storeSlug } = useParams();
  const { vendorStore: vendor } = useVendorStore();
  const { cart, addToCart, updateQuantity, clearCart } = useCart();
  const { wishlistEnabled } = useFeatureFlags();
  const { isAuthenticated } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [allProducts, setAllProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [filters, setFilters] = useState({
    search: "",
    type: "",
    saleType: "",
    minPrice: "",
    maxPrice: "",
  });
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const [cartMismatch, setCartMismatch] = useState(false);
  const [cartMismatchChecked, setCartMismatchChecked] = useState(false);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchBannersPublic()
      .then((items) => {
        if (alive) setBanners(items);
      })
      .catch(() => {
        if (alive) setBanners([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Check cart vendor mismatch once vendor is known
  useEffect(() => {
    if (!vendor || cartMismatchChecked) return;
    setCartMismatchChecked(true);
    if (cart.length > 0) {
      const cartVendorId = cart[0]?.product?.vendorId;
      const thisVendorId = String(vendor._id || vendor.id || "");
      if (cartVendorId && thisVendorId && String(cartVendorId) !== thisVendorId) {
        setCartMismatch(true);
      }
    }
  }, [vendor, cart, cartMismatchChecked]);

  // Fetch products scoped to this vendor
  useEffect(() => {
    if (!storeSlug) return;
    setLoadingProducts(true);
    apiClient("GET", "/api/products", null, { skipLoading: true, params: { storeSlug } })
      .then((data) => setAllProducts(Array.isArray(data?.products) ? data.products : []))
      .catch(() => setAllProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [storeSlug]);

  // Fetch wishlist
  useEffect(() => {
    if (!wishlistEnabled) { setWishlist([]); return; }
    apiClient("GET", "/api/wishlist", null, { skipLoading: true })
      .then((res) => {
        const ids = Array.isArray(res) ? res.map((item) => (item._id ? item._id : item)) : [];
        setWishlist(ids);
      })
      .catch(() => {});
  }, [wishlistEnabled]);

  const toggleWishlist = async (productId) => {
    if (!wishlistEnabled) return;
    if (!isAuthenticated) {
      navigate("/user-login", {
        state: { from: `${location.pathname}${location.search}` },
      });
      return;
    }
    try {
      const res = await apiClient(
        "POST",
        "/api/wishlist/toggle",
        { productId },
        { skipLoading: true },
      );
      if (res?.wishlist) {
        setWishlist(res.wishlist.map((id) => String(id)));
      } else {
        setWishlist((prev) =>
          prev.includes(productId)
            ? prev.filter((id) => id !== productId)
            : [...prev, productId],
        );
      }
    } catch (err) {
      console.error("Wishlist toggle failed:", err.message);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: "", type: "", saleType: "", minPrice: "", maxPrice: "" });
    setFilterOpen(false);
    setCurrentPage(1);
  };

  const allTypes = useMemo(
    () => [...new Set(allProducts.flatMap((p) => p.type || []))].sort(),
    [allProducts],
  );
  const allSaleTypes = useMemo(
    () => [...new Set(allProducts.map((p) => p.saleType).filter(Boolean))],
    [allProducts],
  );

  const filteredProducts = useMemo(() => {
    return allProducts.filter((p) => {
      const q = filters.search.toLowerCase();
      const searchMatch = !q || (p.name || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q);
      const typeMatch = !filters.type ||
        (Array.isArray(p.type) ? p.type.includes(filters.type) : p.type === filters.type);
      const saleMatch = !filters.saleType || p.saleType === filters.saleType;
      const price = effectiveSalePrice(p);
      const minMatch = !filters.minPrice || price >= +filters.minPrice;
      const maxMatch = !filters.maxPrice || price <= +filters.maxPrice;
      return searchMatch && typeMatch && saleMatch && minMatch && maxMatch;
    });
  }, [allProducts, filters]);

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    switch (sortBy) {
      case "price-asc": return list.sort((a, b) => effectiveSalePrice(a) - effectiveSalePrice(b));
      case "price-desc": return list.sort((a, b) => effectiveSalePrice(b) - effectiveSalePrice(a));
      case "name": return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      default:
        return sortProductsWithFeatured(
          list,
          vendor?.featuredProductsEnabled ? vendor?.featuredProductIds : [],
        );
    }
  }, [filteredProducts, sortBy, vendor?.featuredProductIds, vendor?.featuredProductsEnabled]);

  const activeFilterCount = [
    filters.search, filters.type, filters.saleType, filters.minPrice, filters.maxPrice,
  ].filter(Boolean).length;

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = sortedProducts.slice(startIdx, startIdx + PRODUCTS_PER_PAGE);

  const filterPanelProps = {
    filters,
    onFilterChange: handleFilterChange,
    setFilters,
    allTypes,
    allSaleTypes,
    activeFilterCount,
    onClearFilters: clearFilters,
  };

  if (!vendor) return null;

  const storeName = vendor.storeName || vendor.name || "Store";
  const heroBanner = banners[0];
  const bannerCtaTo = heroBanner?.ctaUrl?.trim() || `/${storeSlug}`;

  return (
    <>
      <Helmet>
        <title>{storeName} — Shop</title>
        <meta name="description" content={vendor.bio || `Shop at ${storeName}`} />
      </Helmet>

      {cartMismatch && (
        <CartMismatchModal
          storeName={storeName}
          onClear={() => { clearCart(); setCartMismatch(false); }}
          onKeep={() => setCartMismatch(false)}
        />
      )}

      <div className="min-h-screen relative bg-gradient-to-b from-orange-50/50 via-rose-50/40 to-stone-50 pb-24 sm:pb-10">
        {/* Mobile filter backdrop */}
        {filterOpen && (
          <button
            type="button"
            aria-label="Close filters"
            className="fixed inset-0 z-30 bg-black/40 sm:hidden"
            onClick={() => setFilterOpen(false)}
          />
        )}

        {heroBanner ? (
          <section className="relative overflow-hidden border-b border-rose-100 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
            <div className="pointer-events-none absolute -left-20 top-0 h-56 w-56 rounded-full bg-rose-200/35 blur-3xl" />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-56 w-56 rounded-full bg-amber-200/40 blur-3xl" />
            <div className="relative mx-auto grid max-w-7xl items-center gap-6 px-3 py-6 sm:px-6 sm:py-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
              <div className="text-center lg:text-left">
                <p className="mb-3 inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-rose-600 shadow-sm ring-1 ring-rose-100">
                  Featured
                </p>
                <h1 className="text-3xl font-extrabold tracking-tight text-stone-950 sm:text-5xl">
                  {heroBanner.title}
                </h1>
                {heroBanner.subtitle ? (
                  <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-stone-700 sm:text-base lg:mx-0">
                    {heroBanner.subtitle}
                  </p>
                ) : null}
                <Link
                  to={bannerCtaTo}
                  className="mt-5 inline-flex items-center justify-center rounded-full bg-stone-950 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-stone-900/15 transition hover:-translate-y-0.5 hover:bg-stone-800"
                >
                  {heroBanner.ctaLabel || "Shop now"}
                </Link>
              </div>
              {heroBanner.imageUrl ? (
                <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                  <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-rose-200/50" />
                  <img
                    src={heroBanner.imageUrl}
                    alt={heroBanner.title}
                    className="relative h-56 w-full rounded-[2rem] object-cover shadow-2xl ring-1 ring-white/80 sm:h-72"
                  />
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-6 sm:pt-10">
          {/* Page intro */}
          <div className="mb-8 text-center sm:text-left">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600 mb-2">
              {vendor.storeTagline?.trim() || "Fresh & delivered"}
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              {vendor.storeHeadline?.trim() || "What would you like today?"}
            </h2>
            <p className="mt-2 text-stone-600 text-sm sm:text-base max-w-2xl">
              Browse {storeName}&apos;s products—filter by category, price, or offer. Same fast checkout you love.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            {/* Filters — desktop sidebar */}
            <aside className="hidden lg:block w-full lg:w-72 shrink-0 lg:sticky lg:top-24 lg:self-start">
              <ShopFilterPanel {...filterPanelProps} />
            </aside>

            {/* Filters — mobile bottom sheet */}
            <aside
              className={`fixed inset-x-0 bottom-0 z-40 max-h-[85vh] overflow-y-auto rounded-t-3xl border border-stone-200 bg-white shadow-2xl transition-transform duration-300 sm:hidden ${
                filterOpen ? "translate-y-0" : "translate-y-full pointer-events-none"
              }`}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-stone-100 bg-white px-4 py-3 rounded-t-3xl">
                <span className="font-bold text-stone-900">Filters</span>
                <div className="flex items-center gap-2">
                  {activeFilterCount > 0 && (
                    <button type="button" onClick={clearFilters} className="text-xs font-semibold text-rose-600">
                      Clear all
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setFilterOpen(false)}
                    className="rounded-full p-2 text-stone-500 hover:bg-stone-100"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="px-4 pb-10 pt-2">
                <ShopFilterPanel
                  {...filterPanelProps}
                  embedded
                  className="border-0 shadow-none !rounded-none bg-transparent"
                />
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              {/* Sort row */}
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-stone-900">
                    {loadingProducts ? "Loading menu…" : "On the menu"}
                  </h2>
                  {!loadingProducts && (
                    <p className="text-sm text-stone-500 mt-1">
                      {sortedProducts.length} item{sortedProducts.length === 1 ? "" : "s"}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="sr-only" htmlFor="vendor-sort">Sort by</label>
                  <SelectField
                    id="vendor-sort"
                    name="sortBy"
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                    options={SORT_OPTIONS}
                    triggerClassName="rounded-full py-2 pl-4 pr-4 min-w-[12rem]"
                  />
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="lg:hidden inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-800 shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Products */}
              {loadingProducts ? (
                <div className="flex flex-col gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex animate-pulse gap-4 rounded-2xl border border-stone-100 bg-white p-4">
                      <div className="h-[104px] w-[104px] shrink-0 rounded-xl bg-stone-200 sm:h-[132px] sm:w-[132px]" />
                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-3">
                        <div className="h-3 w-24 rounded bg-stone-200" />
                        <div className="h-5 w-4/5 max-w-xs rounded bg-stone-200" />
                        <div className="h-4 w-20 rounded bg-stone-200" />
                      </div>
                      <div className="hidden w-[148px] shrink-0 flex-col gap-3 border-l border-stone-100 pl-4 sm:flex">
                        <div className="ml-auto h-10 w-10 rounded-full bg-stone-200" />
                        <div className="h-10 w-full rounded-full bg-stone-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortedProducts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 bg-white/80 px-6 py-16 text-center">
                  <div className="text-4xl mb-3" aria-hidden>🍰</div>
                  <h3 className="text-lg font-bold text-stone-900">Nothing matches yet</h3>
                  <p className="text-stone-600 text-sm mt-2 max-w-md mx-auto">
                    Try clearing filters or searching for something else.
                  </p>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-6 rounded-full bg-stone-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-stone-800"
                    >
                      Reset filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-4 md:gap-5">
                    {currentPage === 1 &&
                    vendor?.featuredProductsEnabled &&
                    vendor?.featuredProductIds?.length > 0 &&
                    paginatedProducts.some((p) =>
                      vendor.featuredProductIds.includes(String(p._id)),
                    ) ? (
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-600">
                        Top picks
                      </p>
                    ) : null}
                    {paginatedProducts.map((product) => {
                      const linesForProduct = cart.filter((c) => c.product._id === product._id);
                      const variantQtySum = linesForProduct.reduce((s, l) => s + l.quantity, 0);
                      const hasVar = productHasVariantOptions(product);
                      const item = hasVar ? null : linesForProduct[0];
                      return (
                        <ProductCard
                          key={product._id}
                          product={product}
                          cartItem={item}
                          variantCartQuantitySum={variantQtySum}
                          onAddToCart={() => addToCart(product)}
                          onIncrement={() =>
                            updateQuantity(product._id, 1, linesForProduct[0]?.variantOptionId || "")
                          }
                          onDecrement={() =>
                            updateQuantity(product._id, -1, linesForProduct[0]?.variantOptionId || "")
                          }
                          isWishlisted={wishlist.includes(product._id)}
                          onToggleWishlist={toggleWishlist}
                        />
                      );
                    })}
                  </div>
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  )}
                </>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
}
